import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { supportService } from '../services/supportService';
import { SupportConversation, SupportMessage } from '../types/support';

/** Auth state passed from parent to avoid circular dependency with AuthContext */
export interface SupportChatAuthProps {
  user: { id: string; name?: string; email?: string | null } | null;
  isAuthenticated: boolean;
  isAuthInitialized: boolean;
}

interface SupportChatContextType {
  conversation: SupportConversation | null;
  messages: SupportMessage[];
  loading: boolean;
  error: string | null;
  isConnected: boolean;
  unreadCount: number;
  sendMessage: (message: string) => Promise<void>;
  loadConversation: () => Promise<void>;
  markAsRead: () => Promise<void>;
  closeConversation: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const SupportChatContext = createContext<SupportChatContextType | undefined>(undefined);

export const SupportChatProvider: React.FC<{
  children: React.ReactNode;
  auth: SupportChatAuthProps;
}> = ({ children, auth }) => {
  const { user, isAuthenticated, isAuthInitialized } = auth;
  const [conversation, setConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef<any>(null);
  const sessionUserIdRef = useRef<string | null>(null);

  // Generate or get session-based user ID (independent of authentication)
  useEffect(() => {
    if (!sessionUserIdRef.current) {
      // Generate a session-based user ID and store it in sessionStorage
      // This ID persists for the browser session and is not tied to authentication
      let sessionId = sessionStorage.getItem('supportSessionId');
      if (!sessionId) {
        sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('supportSessionId', sessionId);
      }
      sessionUserIdRef.current = sessionId;
    }
  }, []); // Only run once on mount

  // Get current user ID: use auth user id when logged in (so each user has a separate conversation), else session-based for guests
  const getCurrentUserId = useCallback(() => {
    if (isAuthenticated && user && user.id) {
      return String(user.id);
    }
    return sessionUserIdRef.current || `session-${Date.now()}`;
  }, [isAuthenticated, user]);

  // Get current user name - use authenticated name if available, otherwise default
  const getCurrentUserName = useCallback(() => {
    if (isAuthenticated && user && user.name) {
      return user.name;
    }
    return 'User';
  }, [isAuthenticated, user]);

  // Get current user email - use authenticated email if available
  const getCurrentUserEmail = useCallback(() => {
    if (isAuthenticated && user && user.email) {
      return user.email;
    }
    return null;
  }, [isAuthenticated, user]);

  const refreshUnreadCount = useCallback(async () => {
    // Only refresh unread count for authenticated users
    if (!isAuthenticated || !user) {
      setUnreadCount(0);
      return;
    }

    try {
      const count = await supportService.getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (err) {
      console.error('Error refreshing unread count:', err);
    }
  }, [isAuthenticated, user]);

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthInitialized) {
      return;
    }

    // Support chat connects to gateway (3001); gateway proxies Socket.IO to notification service (3006)
    // Strip /api from base URL so socket path /socket.io resolves correctly
    const base = process.env.REACT_APP_SUPPORT_CHAT_URL ||
                 process.env.REACT_APP_API_BASE_URL ||
                 'http://localhost:3001';
    const supportChatUrl = base.replace(/\/api\/?$/, '').replace(/\/$/, '');
    
    // Check if backend is accessible (optional health check for development)
    if (process.env.NODE_ENV === 'development' && supportChatUrl.includes('localhost')) {
      fetch(`${supportChatUrl}/api/health`)
        .then(() => console.log('✅ Backend health check passed'))
        .catch(() => console.warn('⚠️ Backend health check failed - make sure backend is running on port 3001'));
    }
    
    const socket = io(supportChatUrl, {
      transports: ['websocket', 'polling'], // Allow both transports
      reconnection: true,
      reconnectionAttempts: 10, // Increased attempts
      reconnectionDelay: 2000, // Increased delay
      reconnectionDelayMax: 10000,
      timeout: 20000,
      autoConnect: true,
      forceNew: false, // Reuse existing connection if available
      path: '/socket.io/', // Explicit path for Socket.IO
      upgrade: true, // Allow transport upgrades
    });

    // Set initial connection state based on socket state
    setIsConnected(socket.connected);

    socketRef.current = socket;

    // Monitor connection state periodically to catch state changes
    const connectionCheckInterval = setInterval(() => {
      if (socketRef.current) {
        const isNowConnected = socketRef.current.connected;
        setIsConnected(prevConnected => {
          return isNowConnected;
        });
      }
    }, 1000);

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', (reason: string) => {
      setIsConnected(false);
    });

    socket.on('connect_error', (error: Error) => {
      // Ignore Chrome extension errors (harmless browser extension errors)
      const errorMsg = error.message || String(error);
      if (errorMsg.includes('runtime.lastError') || 
          errorMsg.includes('message port closed') ||
          errorMsg.includes('Extension context invalidated')) {
        // This is a browser extension error, not our code - ignore it
        return;
      }
      
      console.error('❌ Support chat connection error:', error);
      console.error('Error details:', {
        message: error.message,
        description: (error as any).description,
        context: (error as any).context,
        type: (error as any).type,
        url: supportChatUrl,
      });
      setIsConnected(false);
      setError(`Connection failed: ${error.message || 'Unable to connect to support chat server'}`);
    });

    socket.on('reconnect_failed', () => {
      console.error('❌ Failed to reconnect to support chat');
      setIsConnected(false);
    });

    socket.on('new-message', (data: any) => {
      const incomingTime = new Date(data.createdAt || Date.now()).getTime();

      setMessages((prev) => {
        const isDuplicate = prev.some(
          (m) =>
            m.message === data.message &&
            m.sender_type === (data.senderType || 'user') &&
            Math.abs(new Date(m.created_at).getTime() - incomingTime) < 10000
        );
        if (isDuplicate) return prev;

        const newMessage: SupportMessage = {
          id: incomingTime,
          conversation_id: data.conversationId,
          sender_id: data.senderId,
          sender_type: data.senderType,
          sender_name: data.senderName,
          message: data.message,
          is_read: false,
          read_at: null,
          created_at: data.createdAt || new Date().toISOString(),
          attachments: null,
        };
        return [...prev, newMessage];
      });
    });

    socket.on('user-typing', (data: any) => {
      // Handle typing indicator if needed
      console.log('User typing:', data);
    });

    return () => {
      clearInterval(connectionCheckInterval);
      if (socket.connected) {
        socket.disconnect();
      }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthInitialized]); // Socket connection should be stable regardless of user ID changes

  const loadConversation = useCallback(async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('⚠️ Cannot load conversation: no user ID available');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📞 Loading conversation for user:', userId, 'Authenticated:', isAuthenticated);

      // Create or get conversation
      const conv = await supportService.createOrGetConversation({
        userId: userId,
        userName: getCurrentUserName(),
        userEmail: getCurrentUserEmail() || undefined,
      });

      console.log('✅ Conversation loaded:', conv);

      setConversation(conv);

      // Join socket room (only if socket is connected)
      if (socketRef.current && socketRef.current.connected && conv) {
        console.log('🔌 Joining conversation room:', conv.id);
        socketRef.current.emit('join-conversation', {
          conversationId: conv.id,
          userId: userId,
        });
      } else if (socketRef.current && !socketRef.current.connected) {
        console.log('⏳ Waiting for socket connection before joining conversation room');
        // Wait for connection before joining
        socketRef.current.once('connect', () => {
          if (conv) {
            console.log('🔌 Socket connected, joining conversation room:', conv.id);
            socketRef.current.emit('join-conversation', {
              conversationId: conv.id,
              userId: userId,
            });
          }
        });
      } else if (!socketRef.current) {
        console.warn('⚠️ Socket not initialized yet');
      }

      // Don't load message history from DB - only use real-time messages
      setMessages([]);
      
      // Refresh unread count (only if authenticated)
      if (isAuthenticated) {
        await refreshUnreadCount();
      }
    } catch (err) {
      console.error('❌ Error loading conversation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversation';
      setError(errorMessage);
      console.error('Error details:', {
        userId,
        isAuthenticated,
        error: err
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getCurrentUserId, getCurrentUserName, getCurrentUserEmail, refreshUnreadCount]);

  const sendMessage = useCallback(async (message: string) => {
    if (!conversation || !message.trim()) return;

    const userId = getCurrentUserId();
    const userName = getCurrentUserName();
    if (!userId) return;

    let tempMessageId: number | null = null;

    try {
      // Optimistically add message with a unique temporary ID
      const messageTimestamp = Date.now();
      const tempMessage: SupportMessage = {
        id: messageTimestamp,
        conversation_id: conversation.id,
        sender_id: userId,
        sender_type: 'user',
        sender_name: userName,
        message: message.trim(),
        is_read: false,
        read_at: null,
        created_at: new Date().toISOString(),
        attachments: null,
      };
      tempMessageId = tempMessage.id;
      setMessages((prev) => {
        const exists = prev.some(
          (m) =>
            m.message === message.trim() &&
            m.sender_type === 'user' &&
            Math.abs(new Date(m.created_at).getTime() - messageTimestamp) < 10000
        );
        if (exists) {
          return prev;
        }
        return [...prev, tempMessage];
      });

      // Send via socket for real-time only (no DB persistence)
      // Check if socket is connected before sending
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('send-message', {
          conversationId: conversation.id,
          senderId: userId,
          senderType: 'user',
          senderName: userName,
          message: message.trim(),
        });
      } else {
        setError('Not connected to chat server. Please wait for connection...');
        // Remove optimistic message if not connected
        if (tempMessageId) {
          setMessages(prev => prev.filter(m => m.id !== tempMessageId));
        }
        return;
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      // Remove optimistic message on error
      if (tempMessageId) {
        setMessages(prev => prev.filter(m => m.id !== tempMessageId));
      }
    }
  }, [conversation, getCurrentUserId, getCurrentUserName]);

  const markAsRead = useCallback(async () => {
    if (!conversation) return;

    try {
      // No need to mark as read since we're not storing messages in DB
      // Only refresh unread count for authenticated users
      if (isAuthenticated) {
        await refreshUnreadCount();
      }
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  }, [conversation, isAuthenticated, refreshUnreadCount]);

  const closeConversation = useCallback(async () => {
    if (!conversation) return;

    try {
      await supportService.closeConversation(conversation.id);
      setConversation(null);
      setMessages([]);
    } catch (err) {
      console.error('Error closing conversation:', err);
    }
  }, [conversation]);

  // Refresh unread count periodically (only for authenticated users)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUnreadCount(0);
      return;
    }

    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, user, refreshUnreadCount]);

  // Note: Conversation is session-based, not auth-based, so no need to reload on auth changes

  const value: SupportChatContextType = {
    conversation,
    messages,
    loading,
    error,
    isConnected,
    unreadCount,
    sendMessage,
    loadConversation,
    markAsRead,
    closeConversation,
    refreshUnreadCount,
  };

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
};

export const useSupportChat = (): SupportChatContextType => {
  const context = useContext(SupportChatContext);
  if (context === undefined) {
    throw new Error('useSupportChat must be used within a SupportChatProvider');
  }
  return context;
};

